'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, Clock, ClipboardList, Users, Calendar, AlertTriangle } from 'lucide-react';
import { householdsAPI, TimelineEntry } from '@/services/api/households';

const TYPE_CONFIG: Record<string, { icon: React.ElementType; color: string }> = {
  service: { icon: ClipboardList, color: 'text-blue-600 bg-blue-100' },
  member_joined: { icon: Users, color: 'text-green-600 bg-green-100' },
  follow_up: { icon: Calendar, color: 'text-purple-600 bg-purple-100' },
  need: { icon: AlertTriangle, color: 'text-orange-600 bg-orange-100' },
};

interface Props {
  householdId: number;
}

export default function HouseholdTimelineTab({ householdId }: Props) {
  const [events, setEvents] = useState<TimelineEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const loadTimeline = useCallback(async () => {
    setLoading(true);
    try {
      const data = await householdsAPI.getTimeline(householdId);
      setEvents(data);
    } catch (err) {
      console.error('Failed to load timeline:', err);
    } finally {
      setLoading(false);
    }
  }, [householdId]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Clock className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No timeline events yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-1">
      <h3 className="text-lg font-semibold mb-4">Activity Timeline</h3>
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200" />

        <div className="space-y-4">
          {events.map((event, idx) => {
            const config = TYPE_CONFIG[event.type] || TYPE_CONFIG.service;
            const Icon = config.icon;
            return (
              <div key={`${event.type}-${event.id}-${idx}`} className="relative flex items-start gap-4 pl-2">
                <div
                  className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center ${config.color}`}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0 pb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{event.title}</span>
                    <span className="text-xs text-gray-400">
                      {event.date ? new Date(event.date).toLocaleDateString() : ''}
                    </span>
                  </div>
                  {event.subject && (
                    <p className="text-xs text-gray-500">{event.subject}</p>
                  )}
                  {event.description && (
                    <p className="text-xs text-gray-400 mt-0.5">{event.description}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
